/**
 * Penalty shoot-out winner model (acted decision, 13/06/2026):
 * based on the overall quality of each team's starting goalkeeper when known
 * (EA FC 26 overall, src/data/gk-ratings.json), with a deliberately gentle
 * logistic — a 10-point gap ≈ 56 %, 20 points ≈ 61 % — because the empirical
 * literature (268 UEFA shoot-outs, 2025) finds no systematic edge. Whenever
 * either side has no rating, the shoot-out falls back to the scientific
 * default: 50/50.
 */
import type { TeamCode } from "./types.ts";

export type ShootoutModel = "gk" | "fiftyFifty";

/** Map team code -> starting goalkeeper quality (EA FC 26 overall). */
export type GkTable = Readonly<Record<TeamCode, number>>;

export const DEFAULT_GK_DIVISOR = 100;

/**
 * P(home side wins the shoot-out).
 * `fiftyFifty` model or any missing rating → 0.5 exactly.
 */
export function shootoutWinProbability(
  home: TeamCode,
  away: TeamCode,
  model: ShootoutModel,
  gkRatings: GkTable | undefined,
  divisor: number = DEFAULT_GK_DIVISOR,
): number {
  if (model !== "gk") return 0.5;
  const gkHome = gkRatings?.[home];
  const gkAway = gkRatings?.[away];
  if (gkHome === undefined || gkAway === undefined) return 0.5;
  return 1 / (1 + Math.pow(10, -(gkHome - gkAway) / divisor));
}
